import { HttpContext } from '@angular/common/http';
import { Injectable, Type } from '@angular/core';
import {
  ApiQuery,
  BaseApiService,
  ErrorHandlerContext,
} from '@kinet-open-network/client/core/api-client';
import { mapToModel } from '@kinet-open-network/client/shared/data-access/model';
import {
  downloadBlob,
  getFilenameFromHeaders,
} from '@kinet-open-network/client/shared/util';
import {
  ICustomerBalanceHistory,
  IInvoice,
  IInvoiceActionData,
  IInvoiceRelatedDocuments,
} from '@kinet-open-network/shared/data-access/model';
import { format } from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { IExportSipoData } from '../models/api/export-sipo.model';
import { Invoice } from '../models/invoice.model';

@Injectable({
  providedIn: 'root',
})
export class InvoicesService extends BaseApiService<Invoice> {
  override model: Type<Invoice> = Invoice;

  getPdf(id: string) {
    return this.http.get(`${this.endpoint}/${id}/pdf`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  regeneratePdf(id: string) {
    return this.http.post<{ documentId: string }>(
      `${this.endpoint}/${id}/pdf/regenerate`,
      {},
    );
  }

  generateInvoiceItems(id: string) {
    return this.http.post<void>(`${this.endpoint}/${id}/items/generate`, {});
  }

  generateCustomerData(id: string) {
    return this.http
      .post<IInvoice>(`${this.endpoint}/${id}/cust-data/generate`, {})
      .pipe(mapToModel(this.model));
  }

  downloadPdf(id: string) {
    return firstValueFrom(this.getPdf(id)).then((res) => {
      if (res.body) {
        const filename = getFilenameFromHeaders(res.headers);
        return downloadBlob(res.body, filename || 'invoice.pdf');
      }
    });
  }

  refreshItems() {
    return this.http.post<void>(`${this.endpoint}/refresh/items`, {});
  }

  getBalanceHistory(customerId: number, from?: Date, to?: Date) {
    const params: Record<string, string> = {};

    if (from) {
      params['from'] = from.toJSON();
    }
    if (to) {
      params['to'] = to.toJSON();
    }

    return this.http.get<ICustomerBalanceHistory>(
      `${this.endpoint}/${customerId}/balance-history`,
      { params },
    );
  }

  getWithPayments(customerId: number) {
    return this.http.get<{ id: string; type: 'invoice' | 'payment' }[]>(
      `${this.endpoint}/${customerId}/with-payments`,
      {
        context: this.subjectContext({
          defaultResponse: [],
        }),
      },
    );
  }

  runAction(data: IInvoiceActionData) {
    return this.http.post<{ jobId: string }>(`${this.endpoint}/action`, data);
  }

  batchAction(data: IInvoiceActionData, query?: ApiQuery | null) {
    return this.http.post<void>(`${this.endpoint}/batch-action`, data, {
      params: this.apiQueryToHttpParams(query),
    });
  }

  /**
   * @returns count of invoices left out because their e-invoice was not sent
   */
  async massDownload(
    format: 'zip' | 'pdf',
    query?: ApiQuery | null,
  ): Promise<number> {
    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/mass-download`, {
        params: this.apiQueryToHttpParams({
          ...query,
          format,
        }),
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;
    const filename = getFilenameFromHeaders(response.headers);

    await downloadBlob(blob, filename ?? `faktury.${format}`);

    return Number(response.headers.get('X-Skipped-Count') ?? 0);
  }

  async exportSipo(data: IExportSipoData): Promise<void> {
    const params = {
      ...(data.period ? { period: format(data.period, 'yyyy-MM-dd') } : {}),
      ...(data.date ? { date: format(data.date, 'yyyy-MM-dd') } : {}),
    };

    const response = await firstValueFrom(
      this.http.get(`${this.endpoint}/export/sipo`, {
        params,
        responseType: 'blob',
        observe: 'response',
      }),
    );

    const blob = response.body as Blob;
    const filename = getFilenameFromHeaders(response.headers);

    return downloadBlob(
      blob,
      filename ?? `IPZ_<SIPO_CISLO>0000_${format(new Date(), 'yyyyMMdd')}.txt`,
    );
  }

  storno(id: string) {
    return this.http
      .get<Invoice>(`${this.endpoint}/${id}/storno`)
      .pipe(mapToModel(this.model));
  }

  related(id: string) {
    return this.http.get<IInvoiceRelatedDocuments>(
      `${this.endpoint}/${id}/related`,
    );
  }

  /**
   * @param options.excludeId document whose lines don't count as credited, the
   *   one being edited
   * @param options.quiet the caller shows why the invoice can't be credited
   */
  creditNote(
    id: string,
    options: { excludeId?: string; quiet?: boolean } = {},
  ) {
    return this.http
      .get<Invoice>(`${this.endpoint}/${id}/credit-note`, {
        params: options.excludeId ? { excludeId: options.excludeId } : {},
        context: options.quiet
          ? new HttpContext().set(ErrorHandlerContext, {
              ignoreBelowStatus: 500,
            })
          : undefined,
      })
      .pipe(mapToModel(this.model));
  }

  override get endpoint(): string {
    return '/invoicing/invoices';
  }
}
